import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { SearchOutlined } from '@mui/icons-material';
import { Button, InputAdornment, Stack } from '@mui/material';

import {
  fetchOrgMembers,
  loadTeams,
  setContextualSupport,
  unsetContextualSupport,
} from 'js/actions';
import { GET_ORG_MEMBERS_REQUEST, GET_TEAMS_REQUEST } from 'js/constants/actionTypes';
import * as PATHS from 'js/constants/pathnames';
import { SUPPORT_KEY_ACCOUNT } from 'js/constants/supportPageKeys';
import useAppDispatch from 'js/hooks/useAppDispatch';

import { AddTeam } from 'new/components/settings/Teams/AddTeam';
import { TeamDetailSettings } from 'new/components/TeamDetail/TeamDetail';
import { TeamsTable } from 'new/components/TeamsTable';
import { Tabs } from 'new/elements/Tabs';
import { TextField } from 'new/elements/TextField';
import PageLayout from 'new/elements/pageLayout/PageLayout';
import { Team } from 'new/entities/Team';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { useTeams } from 'new/hooks/adapters/useTeams';
import { useDistance } from 'new/hooks/useDistance';
import { useFilteredData } from 'new/hooks/useFilteredData';
import { useModals } from 'new/hooks/useModals';
import { router } from 'new/routes/router';
import { ScreenComponent } from 'new/routes/RouterManager';
import { ReactComponent as PlusIcon } from 'new/components/icons/fi-rr-plus.svg';

import { columns } from './columns';
import styles from './Teams.module.scss';

interface TeamsProps {}

function CreateTeam() {
  const { openModal } = useModals();

  const onClick = () => {
    openModal(AddTeam.modalConfig.name);
  };

  return (
    <Button
      onClick={onClick}
      variant="contained"
      className={styles.addTeamButton}
      startIcon={<PlusIcon />}
    >
      New Team
    </Button>
  );
}

function Search({ onSearchChange }) {
  return (
    <TextField
      className={styles.search}
      placeholder="Find Teams"
      onChange={onSearchChange}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchOutlined />
          </InputAdornment>
        ),
      }}
    />
  );
}

function RightView({ onSearchChange }) {
  return (
    <Stack direction="row" spacing={2} justifyContent="space-between" width="100%" marginTop={3}>
      <Search onSearchChange={onSearchChange} />
      <CreateTeam />
    </Stack>
  );
}

export const Teams: ScreenComponent<TeamsProps> = () => {
  const [tab, setTab] = useState('my');
  const ref = useRef(null);
  const distance = useDistance(ref, 400);
  const fullTeams = useTeams();
  const dispatch = useAppDispatch();
  const org = useCurrentOrg();
  const user = useCurrentUser();
  const history = useHistory();
  const loading = useIsLoading(GET_TEAMS_REQUEST, GET_ORG_MEMBERS_REQUEST);
  useEffect(() => {
    dispatch(setContextualSupport(SUPPORT_KEY_ACCOUNT));
    return function cleanup() {
      dispatch(unsetContextualSupport(SUPPORT_KEY_ACCOUNT));
    };
  }, []);

  const { my, other } = useMemo(() => {
    return fullTeams.reduce(
      (data, team) => {
        if (team.members.some((member) => member.id === user.id)) {
          data.my.push(team);
        } else {
          data.other.push(team);
        }
        return data;
      },
      { my: [], other: [] },
    );
  }, [fullTeams]);
  const teams = tab === 'my' ? my : other;
  const { filteredData, onSearchChange } = useFilteredData(teams, 'name');

  useEffect(() => {
    dispatch(loadTeams());
    dispatch(fetchOrgMembers(org.id));
  }, []);

  const onTabChange = (e, value) => {
    setTab(value);
  };

  const onClickTeam = (team: Team) => {
    history.push(
      router.manager.getPath(TeamDetailSettings.screenConfig.screenName, 'settings', [team.id]),
    );
  };

  const tabs = [
    {
      label: 'Your teams',
      value: 'my',
    },
  ];
  if (user.isAdmin) {
    tabs.push({
      label: 'Other teams',
      value: 'other',
    });
  }

  return (
    <PageLayout
      title="Teams"
      ref={ref}
      className={styles.container}
      bottomView={<RightView onSearchChange={onSearchChange} />}
    >
      {user?.isAdmin ? (
        <div className={styles.tabs}>
          <Tabs value={tab} tabs={tabs} defaultValue="my" onChange={onTabChange} />
        </div>
      ) : null}
      <TeamsTable
        height={distance}
        teams={filteredData}
        loading={loading}
        onClickTeam={onClickTeam}
        columns={columns}
        headClassName={styles.headClassName}
      />
    </PageLayout>
  );
};

Teams.screenConfig = {
  path: `${PATHS.ROUTE_REDESIGN}/settings/teams`,
  screenName: 'settings.Teams',
  modals: [AddTeam],
  header: {
    title: 'Settings',
    slugTitle: 'Teams',
  },
  domain: 'settings',
};
